/**
 * Transient notifications.
 *
 * Contract kept from the first version — `useToast().push('Saved.', 'ok')` — with
 * a richer object form for everything else:
 *
 *   toast.push({ title: 'Draft generated', detail: '4 issues · 2 withheld',
 *                tone: 'ok', action: { label: 'Open', onClick: … } })
 *
 * Behaviour worth knowing:
 * - Errors are sticky. An `ok` toast is a receipt and can disappear; a failure
 *   is information the user has not acted on yet, so it waits to be dismissed.
 * - Identical messages collapse into one with a counter instead of stacking —
 *   a retry loop cannot paper over the screen.
 * - At most {@link MAX_VISIBLE} are shown; the oldest is dropped first.
 * - Everything pushed is also written to the client log, so the Diagnostics
 *   screen shows what the user was told and when.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { logger } from '@/lib/logger';
import { ApiClientError } from '@/lib/api';
import { Icon, type IconName } from './icons';

export type ToastTone = 'default' | 'info' | 'ok' | 'warn' | 'danger';

export interface ToastOptions {
  title: string;
  /** Second line: the specifics — counts, a correlation id, what to do next. */
  detail?: string;
  tone?: ToastTone;
  /** One inline action. Dismisses the toast after running. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds before auto-dismiss. `0` pins it until dismissed. */
  duration?: number;
}

interface ToastRecord extends Required<Pick<ToastOptions, 'title'>> {
  id: number;
  tone: ToastTone;
  detail?: string;
  action?: ToastOptions['action'];
  duration: number;
  count: number;
}

interface ToastApi {
  /** String form (legacy, still the common case) or the full options object. */
  push: (message: string | ToastOptions, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
  clear: () => void;
}

const MAX_VISIBLE = 5;
/** Receipts fade; warnings linger; failures wait to be acknowledged. */
const DEFAULT_DURATION: Record<ToastTone, number> = {
  default: 4200,
  info: 4200,
  ok: 3600,
  warn: 7000,
  danger: 0,
};
const ICON: Record<ToastTone, IconName> = {
  default: 'Info',
  info: 'Info',
  ok: 'Check',
  warn: 'Alert',
  danger: 'Alert',
};

const ToastContext = createContext<ToastApi | null>(null);
const log = logger.child('toast');

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback((message: string | ToastOptions, toneArg?: ToastTone) => {
    const opts: ToastOptions = typeof message === 'string' ? { title: message, tone: toneArg } : message;
    const tone = opts.tone ?? toneArg ?? 'default';
    const duration = opts.duration ?? DEFAULT_DURATION[tone];

    log[tone === 'danger' ? 'error' : tone === 'warn' ? 'warn' : 'info'](opts.title, {
      tone,
      ...(opts.detail ? { detail: opts.detail } : {}),
    });

    setToasts((current) => {
      // Collapse a repeat of the newest toast rather than stacking duplicates.
      const last = current[current.length - 1];
      if (last && last.title === opts.title && last.detail === opts.detail && last.tone === tone) {
        return [...current.slice(0, -1), { ...last, count: last.count + 1 }];
      }
      const id = ++seq.current;
      if (duration > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => {
            timers.current.delete(id);
            setToasts((t) => t.filter((x) => x.id !== id));
          }, duration),
        );
      }
      const next = [...current, { id, title: opts.title, detail: opts.detail, tone, action: opts.action, duration, count: 1 }];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
  }, []);

  const clear = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current.clear();
    setToasts([]);
  }, []);

  // Never let a pending timer fire against an unmounted provider.
  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const api = useMemo(() => ({ push, dismiss, clear }), [push, dismiss, clear]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-wrap" aria-live="polite" aria-label="Notifications">
        {toasts.map((t) => {
          const Glyph = Icon[ICON[t.tone]];
          return (
            <div
              key={t.id}
              className={`toast ${t.tone === 'default' ? '' : t.tone}`.trim()}
              // Failures interrupt the reader; receipts wait their turn.
              role={t.tone === 'danger' ? 'alert' : 'status'}
              aria-live={t.tone === 'danger' ? 'assertive' : 'polite'}
            >
              <Glyph className="toast-icon" aria-hidden />
              <div className="toast-body">
                <div className="toast-title">
                  {t.title}
                  {t.count > 1 && <span className="muted t-xs"> ×{t.count}</span>}
                </div>
                {t.detail && <div className="toast-detail">{t.detail}</div>}
                {t.action && (
                  <button
                    type="button"
                    className="toast-action"
                    onClick={() => {
                      dismiss(t.id);
                      t.action?.onClick();
                    }}
                  >
                    {t.action.label}
                  </button>
                )}
              </div>
              <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
                <Icon.Close aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/** Pulls a human message out of any thrown value. */
export function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}

/**
 * The second line of an error toast: the status code and the correlation id a
 * user can quote to support. Empty when there is nothing useful to add.
 */
export function errorDetail(e: unknown): string | undefined {
  if (!(e instanceof ApiClientError)) return undefined;
  const bits = [e.status ? `HTTP ${e.status}` : 'no response', e.correlationId && `ref ${e.correlationId}`].filter(Boolean);
  return bits.length ? bits.join(' · ') : undefined;
}

/** The standard shape for reporting a failed mutation. */
export function errorToast(e: unknown, title?: string): ToastOptions {
  return {
    title: title ?? errorMessage(e),
    ...(title ? { detail: [errorMessage(e), errorDetail(e)].filter(Boolean).join(' · ') } : { detail: errorDetail(e) }),
    tone: 'danger',
  };
}
